import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import DashboardEventsEmpty from '@/features/dashboard/components/DashboardEvents/DashboardEventsEmpty';
import EActivitiesListSkeleton from '@/components/everridge/activities/EActivitiesListSkeleton';
import { useDashboardRecentEventsApi } from '@/features/dashboard/hooks/useDashboardRecentEventsApi';
import { cn } from '@/lib/utils';
import EActivitiesElement from '@/components/everridge/activities/EActivitiesElement';
import { DashboardUtils } from '@/features/dashboard/utils/dashboardUtils';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import ReservationDetails from '@/features/reservations/components/ReservationDetails';
import { DocumentType } from '@/features/calendar/types/calendar-dto';
import ReservationManage from '@/features/reservations/components/ReservationManage';

type Props = {
	isMobile?: boolean;
};

const DashboardEvents: FC<Props> = ({ isMobile }) => {
	const [showReservationDetails, setShowReservationDetails] = useState(false);
	const [showReservationManage, setShowReservationManage] = useState(false);
	const [eventId, setEventId] = useState('');
	const [reservationApartmentId, setReservationApartmentId] = useState('');
	const [eventType, setEventType] = useState<DocumentType>();

	const navigate = useNavigate();
	const { t } = useTranslation();

	const { recentEventsData, isLoadingRecentEventsData } =
		useDashboardRecentEventsApi();

	const handleShowEventDetails = (id: string, type?: DocumentType, apartmentId?: string) => {
		setEventId(id);
		setReservationApartmentId(apartmentId || '');
		setEventType(type);
		setShowReservationDetails(true);
	};

	const handleShowEditReservation = (id: string) => {
		setShowReservationDetails(false);
		setReservationApartmentId(id);
		setShowReservationManage(true);
	};

	return (
		<div className='grow flex flex-col gap-4'>
			<div className='flex-1'>
				{isLoadingRecentEventsData && (
					<EActivitiesListSkeleton isMobile={isMobile} />
				)}
				{!isLoadingRecentEventsData &&
					!!recentEventsData?.groups?.length && (
						<div
							className={cn(
								'flex flex-col gap-6',
								isMobile && 'gap-4',
							)}>
							{recentEventsData?.groups.map(
								({ date, documents }) => {
									return (
										<div key={`${date}`}>
											<h5 className='text-sm text-popover-800'>
												{DateTimeHelper.formatDate(
													new Date(date),
													2,
												)}
											</h5>
											{documents?.map((event) => {
												const {
													text,
													icon,
													colorClass,
												} =
													DashboardUtils.getEventsConfig(
														event.eventType,
													);

												const header = `${DateTimeHelper.formatDate(
													event.checkInDate,
												)} - ${DateTimeHelper.formatDate(
													event.checkOutDate,
												)}`;

												const descriptionText = `${
													event.apartmentName
												}\u00A0\u00A0\u00A0·\u00A0\u00A0\u00A0${t(
													text,
												)}`;

												return (
													<EActivitiesElement
														key={event.documentId}
														icon={icon}
														colorClass={colorClass}
														header={header}
														description={
															descriptionText
														}
														value={event.revenue}
														id={event.documentId}
														apartmentId={event.apartmentId}
														type={
															event.documentType
														}
														onClick={
															handleShowEventDetails
														}
														isMobile={isMobile}
														currencyAssetType='calendar'
													/>
												);
											})}
										</div>
									);
								},
							)}
						</div>
					)}
				{!isLoadingRecentEventsData &&
					!recentEventsData?.groups?.length && (
						<DashboardEventsEmpty />
					)}
			</div>
			<Button
				variant={'outline'}
				className='w-full text-sm font-medium'
				onClick={() => navigate('/calendar')}>
				{t(
					'main.dashboard.components.recentActivities.list.events.seeMoreButton',
				)}
			</Button>
			{showReservationDetails && (
				<ReservationDetails
					id={eventId}
					onEdit={handleShowEditReservation}
					onClose={() => setShowReservationDetails(false)}
					isMobile={isMobile}
				/>
			)}
			{showReservationManage && (
				<ReservationManage
					apartmentId={reservationApartmentId}
					reservationId={eventId}
					onClose={() => setShowReservationManage(false)}
				/>
			)}
		</div>
	);
};

export default DashboardEvents;
